import React, { useMemo, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import Switch from '../../../../components/Switch';
import { currentModulesHandle } from '../../../../utils/CurrentModulesHandle';
import { ACTION_INFO, MODULE_GROUPS, SPECIAL_INFO, moduleInfo } from './moduleCatalog';

const C = { panel: 'rgb(var(--panel))', line: 'rgb(var(--fg) / 0.07)', text: 'rgb(var(--fg))', muted: 'rgb(var(--fg-dim))', faint: 'rgb(var(--fg-faint))' };

/** Une la lista base de módulos con lo que trae el proyecto (permisos nuevos aparecen apagados). */
export function mergeModules(current) {
  return currentModulesHandle.map((base) => {
    const existing = current?.find((m) => m.module === base.module);
    if (!existing) return base;
    return {
      ...base,
      ...existing,
      actions: { ...base.actions, ...(existing.actions || {}) },
      special_actions: { ...base.special_actions, ...(existing.special_actions || {}) },
    };
  });
}

function PermissionToggle({ id, label, help, checked, disabled, onChange }) {
  return (
    <div className="flex items-start gap-2.5 py-1.5">
      <Switch id={id} size="sm" checked={checked} disabled={disabled} onChange={onChange} label={label} />
      <label htmlFor={id} className="text-[13px] leading-tight cursor-pointer" style={{ color: disabled ? C.faint : C.text }}>
        {label}
        {help && <span className="block text-xs mt-0.5" style={{ color: C.muted }}>{help}</span>}
      </label>
    </div>
  );
}

/**
 * Lista de módulos agrupada por tema. Cada módulo se activa con su interruptor y,
 * si está activo, muestra qué puede hacer el cliente dentro de él.
 */
export default function ModulesPicker({ modules, onChange, idPrefix = 'mod' }) {
  const merged = useMemo(() => mergeModules(modules), [modules]);
  const [query, setQuery] = useState('');
  const [onlyActive, setOnlyActive] = useState(false);

  const update = (moduleKey, fn) => {
    onChange(merged.map((m) => (m.module === moduleKey ? fn(JSON.parse(JSON.stringify(m))) : m)));
  };

  const q = query.trim().toLowerCase();
  const visible = merged.filter((m) => {
    if (onlyActive && !m.view) return false;
    if (!q) return true;
    const info = moduleInfo(m.module);
    return `${info.name} ${info.description} ${m.module}`.toLowerCase().includes(q);
  });
  const activeCount = merged.filter((m) => m.view).length;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <label className="relative flex-1 min-w-[220px]">
          <span className="sr-only">Buscar módulo</span>
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-xs" style={{ color: C.faint }} aria-hidden />
          <input
            type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar módulo"
            className="w-full rounded-xl pl-9 pr-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF]"
            style={{ backgroundColor: 'rgb(var(--raised))', border: `1px solid ${C.line}`, color: C.text }}
          />
        </label>
        <div className="flex rounded-xl p-0.5" style={{ backgroundColor: 'rgb(var(--raised))', border: `1px solid ${C.line}` }} role="group" aria-label="Filtrar módulos">
          {[{ v: false, l: 'Todos' }, { v: true, l: `Activos (${activeCount})` }].map((o) => (
            <button
              key={o.l} type="button" onClick={() => setOnlyActive(o.v)} aria-pressed={onlyActive === o.v}
              className="rounded-lg px-3 py-1.5 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF]"
              style={onlyActive === o.v ? { backgroundColor: 'rgb(var(--fg) / 0.08)', color: C.text } : { color: C.muted }}
            >
              {o.l}
            </button>
          ))}
        </div>
      </div>

      {MODULE_GROUPS.map((g) => {
        const items = visible.filter((m) => moduleInfo(m.module).group === g.key);
        if (items.length === 0) return null;
        return (
          <section key={g.key} aria-labelledby={`${idPrefix}-g-${g.key}`}>
            <h4 id={`${idPrefix}-g-${g.key}`} className="text-[13px] font-semibold mb-2" style={{ color: C.muted }}>{g.label}</h4>
            <ul className="rounded-2xl overflow-hidden" style={{ backgroundColor: C.panel, border: `1px solid ${C.line}` }}>
              {items.map((m, i) => {
                const info = moduleInfo(m.module);
                const actions = Object.entries(m.actions || {});
                const specials = Object.entries(m.special_actions || {});
                const switchId = `${idPrefix}-${m.module.replace(/\s+/g, '-')}`;
                return (
                  <li key={m.module} style={{ borderTop: i ? `1px solid ${C.line}` : 'none' }}>
                    <div className="flex items-start gap-3 px-4 py-3.5">
                      <Switch
                        id={switchId} checked={m.view} label={`Mostrar ${info.name}`}
                        onChange={(v) => update(m.module, (x) => ({ ...x, view: v }))}
                      />
                      <label htmlFor={switchId} className="flex-1 cursor-pointer">
                        <span className="block text-sm font-medium" style={{ color: m.view ? C.text : 'rgb(var(--fg-dim))' }}>{info.name}</span>
                        {info.description && <span className="block text-xs mt-0.5 max-w-[62ch]" style={{ color: C.muted }}>{info.description}</span>}
                      </label>
                    </div>

                    {m.view && (actions.length > 0 || specials.length > 0) && (
                      <div className="px-4 pb-4 pl-[66px]">
                        <p className="text-xs mb-1" style={{ color: C.faint }}>El cliente puede:</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                          {actions.map(([k, v]) => (
                            <PermissionToggle
                              key={k} id={`${switchId}-a-${k}`} label={ACTION_INFO[k]?.label ?? k} checked={!!v}
                              onChange={(val) => update(m.module, (x) => ({ ...x, actions: { ...x.actions, [k]: val } }))}
                            />
                          ))}
                          {specials.map(([k, v]) => {
                            const meta = SPECIAL_INFO[k] ?? { label: k };
                            const blocked = meta.requires && !m.special_actions?.[meta.requires];
                            return (
                              <PermissionToggle
                                key={k} id={`${switchId}-s-${k}`} label={meta.label}
                                help={blocked ? `Primero activa “${SPECIAL_INFO[meta.requires]?.label}”.` : meta.help}
                                checked={!!v && !blocked} disabled={!!blocked}
                                onChange={(val) => update(m.module, (x) => {
                                  const sa = { ...x.special_actions, [k]: val };
                                  // Al apagar un permiso, se apagan los que dependen de él
                                  Object.entries(SPECIAL_INFO).forEach(([dk, dm]) => { if (dm.requires === k && !val) sa[dk] = false; });
                                  return { ...x, special_actions: sa };
                                })}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}

      {visible.length === 0 && (
        <p className="text-sm py-6 text-center" style={{ color: C.muted }}>
          Ningún módulo coincide con “{query}”.
        </p>
      )}
    </div>
  );
}
